import { logger as garnishLogger } from '@garnish/logger';
import { assign } from 'xstate';

import {
  Action,
  type ClaimErrorType,
  type FetchReceiptResponse,
  type ReceiptErrorType,
} from '../receipt.machine.types';
import { getEventErrorMessage, toErrorMessage } from './receipt.machine.utils';

// ─── Actions ─────────────────────────────────────────────────────────────────

const assignReceiptData: Action<FetchReceiptResponse> = assign((_args, params) => {
  const { receipt } = params;

  if (receipt?.__typename !== 'Receipt') {
    return {};
  }

  const order = receipt.order;

  return { order };
});

const assignError: Action = assign(({ event }) => {
  const raw = 'error' in event ? event.error : undefined;

  return { error: toErrorMessage(raw) ?? UNKNOWN_ERROR_MESSAGE };
});

// ─── Log Actions ─────────────────────────────────────────────────────────────

const logReceiptViewed: Action<{ isClaimed: boolean }> = ({ context }, params) => {
  receiptMachineLog.info('Viewed a receipt', {
    orderId: context.order?.id,
    receiptId: context.receiptId,
    isClaimed: params.isClaimed,
  });
};

const logSuccessfulClaim: Action = (args) => {
  const { order, receiptId } = args.context;

  receiptMachineLog.info('Successfully claimed a receipt', { orderId: order?.id, receiptId });
};

const logFailedClaim: Action = ({ context, event }) => {
  receiptMachineLog.error('Failed to claim a receipt', {
    orderId: context.order?.id,
    receiptId: context.receiptId,
    errorMessage: getEventErrorMessage(event),
  });
};

const logFailedClaimDueToUserError: Action<{ errorType: ClaimErrorType }> = (
  { context, event },
  params,
) => {
  receiptMachineLog.warn('Failed to claim a receipt due to user error', {
    errorType: params.errorType,
    orderId: context.order?.id,
    receiptId: context.receiptId,
    errorMessage: getEventErrorMessage(event),
  });
};

const logFailedReceiptFetch: Action = ({ context, event }) => {
  receiptMachineLog.error('Failed to fetch a receipt', {
    orderId: context.order?.id,
    receiptId: context.receiptId,
    errorMessage: getEventErrorMessage(event),
  });
};

const logFailedReceiptFetchDueToUserError: Action<{ errorType: ReceiptErrorType }> = (
  { context, event },
  params,
) => {
  receiptMachineLog.error('Failed to fetch a receipt due to user error', {
    errorType: params.errorType,
    orderId: context.order?.id,
    receiptId: context.receiptId,
    errorMessage: getEventErrorMessage(event),
  });
};

// ─── External Actions ────────────────────────────────────────────────────────

const onSuccessfulClaim: Action = () => {};
const onReceiptError: Action<{ userError: ReceiptErrorType }> = () => {};
const onClaimError: Action<{ userError: ClaimErrorType }> = () => {};

// ─────────────────────────────────────────────────────────────────────────────

export const Actions = {
  assignReceiptData,
  assignError,

  onSuccessfulClaim,
  onReceiptError,
  onClaimError,

  logSuccessfulClaim,
  logReceiptViewed,
  logFailedClaim,
  logFailedClaimDueToUserError,
  logFailedReceiptFetch,
  logFailedReceiptFetchDueToUserError,
};

// ─── Logger ───────────────────────────────────────────────────────────────────

export const receiptMachineLog = garnishLogger.extend('RECEIPT_MACHINE');

// ─── Constants ───────────────────────────────────────────────────────────────

const UNKNOWN_ERROR_MESSAGE = 'Unknown error';
